/**
 * The toast itself, hosted above every authenticated screen.
 *
 * It announces rather than interrupts: the success is already obvious from landing on the tracking
 * screen, so what this adds is the **amount**. `role="status"` is the right politeness for that —
 * an alert would cut into whatever a screen reader was saying about the order that just appeared.
 *
 * It leaves on its own after a few seconds and can be dismissed by tapping it. No action inside:
 * a toast with a button is a dialog wearing a disguise.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { dismissPaidToast, readPaidToast, subscribeToPaidToast } from './paidToastStore';

/** Long enough to read a sum without re-reading it, short enough not to sit over the map. */
const VISIBLE_MS = 4000;

export function PaidToast() {
  const text = useSyncExternalStore(subscribeToPaidToast, readPaidToast, readPaidToast);

  useEffect(() => {
    if (text === null) return;
    const timer = setTimeout(dismissPaidToast, VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [text]);

  if (text === null) return null;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 z-50 mx-auto flex justify-center px-4"
      // `mx-auto` + the app's own width: the toast belongs to the column, not to the window.
      style={{
        maxWidth: 'var(--app-width)',
        bottom: 'calc(env(safe-area-inset-bottom) + 84px)',
      }}
    >
      <button
        type="button"
        role="status"
        aria-live="polite"
        onClick={dismissPaidToast}
        /* `success-soft` + `text-ink`, not white-on-`success`: the success colour flips to a
           light green in dark mode, where white type on it is unreadable. Same pairing every
           success surface in this app already uses — see `Banner`. */
        className="pointer-events-auto max-w-full truncate rounded-full border border-success bg-success-soft px-4 py-2.5 text-[14px] font-bold text-ink shadow-lg"
      >
        {text}
      </button>
    </div>
  );
}
